import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminDiscountSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/discount-settings").then((res) => setSettings(res.data));
  }, []);

  const update = (field, value) => setSettings((s) => ({ ...s, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/admin/discount-settings", settings);
      setSettings(data);
      toast.success("First-time discount settings saved.");
    } catch {
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <div data-testid="admin-discount-settings-page" className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">First-Time Discount Settings</h1>
        <Button onClick={handleSave} disabled={saving} className="rounded-full btn-lift" data-testid="admin-discount-save-btn">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Label>Promotion Active</Label>
            <p className="text-xs text-slate-500 mt-0.5">Turn the popup and email codes on or off site-wide.</p>
          </div>
          <Switch checked={settings.active} onCheckedChange={(v) => update("active", v)} data-testid="admin-discount-active-toggle" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Discount Type</Label>
            <Select value={settings.discount_type} onValueChange={(v) => update("discount_type", v)}>
              <SelectTrigger data-testid="admin-discount-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage Off</SelectItem>
                <SelectItem value="fixed">Fixed Dollar Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Discount Value</Label>
            <Input type="number" min={0} value={settings.discount_value} onChange={(e) => update("discount_value", parseFloat(e.target.value) || 0)} data-testid="admin-discount-value-input" />
          </div>
          <div>
            <Label>Code Expiration (days)</Label>
            <Input type="number" min={0} value={settings.expiration_days} onChange={(e) => update("expiration_days", parseInt(e.target.value) || 0)} data-testid="admin-discount-expiration-input" />
          </div>
          <div>
            <Label>Minimum Purchase Amount ($)</Label>
            <Input type="number" min={0} value={settings.min_purchase_amount} onChange={(e) => update("min_purchase_amount", parseFloat(e.target.value) || 0)} data-testid="admin-discount-min-purchase-input" />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label>Combinable With Other Codes</Label>
            <p className="text-xs text-slate-500 mt-0.5">Note: the checkout currently supports one coupon code per order regardless of this setting.</p>
          </div>
          <Switch checked={settings.combinable} onCheckedChange={(v) => update("combinable", v)} data-testid="admin-discount-combinable-toggle" />
        </div>

        <div className="border-t border-slate-100 pt-6 space-y-4">
          <p className="text-sm font-semibold text-slate-900">Popup Content</p>
          <div>
            <Label>Popup Headline</Label>
            <Input value={settings.popup_headline} onChange={(e) => update("popup_headline", e.target.value)} data-testid="admin-discount-headline-input" />
          </div>
          <div>
            <Label>Popup Description</Label>
            <Textarea rows={2} value={settings.popup_description} onChange={(e) => update("popup_description", e.target.value)} data-testid="admin-discount-description-input" />
          </div>
          <div>
            <Label>Floating Button Text</Label>
            <Input value={settings.floating_button_text} onChange={(e) => update("floating_button_text", e.target.value)} data-testid="admin-discount-button-text-input" />
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6 space-y-4">
          <p className="text-sm font-semibold text-slate-900">Email Content</p>
          <div>
            <Label>Email Subject Line</Label>
            <Input value={settings.email_subject} onChange={(e) => update("email_subject", e.target.value)} data-testid="admin-discount-email-subject-input" />
          </div>
          <div>
            <Label>Email Intro Text</Label>
            <Textarea rows={3} value={settings.email_intro_text} onChange={(e) => update("email_intro_text", e.target.value)} data-testid="admin-discount-email-intro-input" />
          </div>
        </div>
      </div>
    </div>
  );
}
